import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clapperboard, Download, Share2, Sparkles } from "lucide-react";
import { api, downloadFile, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";
import { ShareStoryDialog } from "@/components/insights/ShareStoryDialog";
import { YearVideo } from "@/components/insights/YearVideo";
import { useAuth } from "@/context/AuthContext";

const Strip = ({ months }) => (
  <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5 mt-4" data-testid="year-strip">
    {months.map((m) => (
      <div key={m.month_key} className="mm-soft !rounded-xl p-1.5 text-center min-w-0" title={m.top ? `${m.top.name} · ${m.checkins} check-ins` : "No check-ins"} data-testid={`year-month-${m.month_key}`}>
        <div className="text-[10px] font-semibold text-mm-muted">{m.label}</div>
        <div className="text-lg my-0.5">{m.top ? m.top.emoji : "·"}</div>
        <div className="text-[10px] text-mm-primary font-semibold">{m.checkins || ""}</div>
      </div>
    ))}
  </div>
);

const Highlights = ({ y }) => {
  const items = [
    y.best_month && ["year-best-month", `${y.best_month} was your brightest month.`],
    y.toughest_month && ["year-toughest-month", `${y.toughest_month} felt the heaviest — and you kept showing up.`],
    y.best_skill && ["year-best-skill", `${y.best_skill.name} helped most — stress dropped ${y.best_skill.avg_drop} on average (${y.best_skill.uses}×).`],
  ].filter(Boolean);
  return items.length ? <ul className="mt-4 space-y-1.5 text-sm list-disc pl-5">{items.map(([id, t]) => <li key={id} data-testid={id}>{t}</li>)}</ul> : null;
};

export const YearInReview = () => {
  const [period, setPeriod] = useState("last12");
  const [y, setY] = useState(null);
  const [dl, setDl] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [video, setVideo] = useState(false);
  const [latest, setLatest] = useState(null);
  useEffect(() => {
    if (!video) api.get("/year-story/videos").then((r) => setLatest(r.data[0] || null)).catch((e) => logError("Videos failed:", e));
  }, [video]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/year-story", { params: { period } }).then((r) => setY(r.data)).catch((e) => logError("Year story failed:", e));
  }, [period]);

  const pdf = async () => {
    setDl(true);
    try { await downloadFile(`/year-story/pdf?period=${period}`, `mindfulme-year-${period}.pdf`); toast.success("PDF downloaded"); }
    catch (e) { toast.error(errMsg(e, "Could not make the PDF")); } finally { setDl(false); }
  };

  const sharePayload = useMemo(() => ({ period }), [period]);
  if (!y) return null;
  const year = period === "last12" ? y.years[0] : Number(period);
  const i = y.years.indexOf(year);
  const chip = (on) => `mm-chip !py-1.5 !px-3 text-sm ${on ? "mm-chip-on" : ""}`;
  const arrow = "p-1.5 rounded-full hover:bg-mm-soft disabled:opacity-30 transition-colors";
  return (
    <div className="mm-card p-5" data-testid="year-in-review">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold flex items-center gap-2 whitespace-nowrap"><Sparkles size={18} /> Year in review</div>
        <div className="flex items-center gap-1.5">
          <button className={chip(period === "last12")} onClick={() => setPeriod("last12")} data-testid="year-period-last12">Last 12 months</button>
          <button className={arrow} disabled={period === "last12" || i >= y.years.length - 1} onClick={() => setPeriod(String(y.years[i + 1]))} aria-label="Previous year" data-testid="year-prev"><ChevronLeft size={16} /></button>
          <button className={chip(period !== "last12")} onClick={() => setPeriod(String(year))} data-testid="year-period-year">{year}</button>
          <button className={arrow} disabled={period === "last12" || i <= 0} onClick={() => setPeriod(String(y.years[i - 1]))} aria-label="Next year" data-testid="year-next"><ChevronRight size={16} /></button>
        </div>
      </div>
      <p className="text-sm text-mm-muted mt-1" data-testid="year-label">{y.label} · {y.checkins} check-in{y.checkins === 1 ? "" : "s"}</p>
      <Strip months={y.months} />
      {y.checkins ? (
        <>
          {y.top_emotions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4" data-testid="year-top-emotions">
              {y.top_emotions.map((e) => <span key={e.name} className="mm-chip !cursor-default !py-1 text-sm">{e.emoji} {e.name} · {e.count}</span>)}
            </div>
          )}
          <Highlights y={y} />
          <div className="flex flex-wrap gap-2 mt-5">
            <button className="mm-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" disabled={dl} onClick={pdf} data-testid="year-pdf"><Download size={15} /> {dl ? "Preparing…" : "Download PDF"}</button>
            <button className="mm-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" onClick={() => setSharing(true)} data-testid="year-share"><Share2 size={15} /> Share with supporters</button>
            <button className="mm-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" onClick={() => setVideo(true)} data-testid="year-video"><Clapperboard size={15} /> Make a video</button>
          </div>
          {latest && (
            <button className="text-xs text-mm-muted mt-3 hover:text-mm-primary transition-colors" onClick={() => setVideo(true)} data-testid="year-video-watch-count">
              🎬 Your year video: watched by {latest.shared_with.filter((w) => w.watched).length} of {latest.shared_with.length} supporter{latest.shared_with.length === 1 ? "" : "s"}
            </button>
          )}
          {video && <YearVideo y={y} name={(user?.name || "My").split(" ")[0]} onClose={() => setVideo(false)} />}
          {sharing && <ShareStoryDialog multi endpoint="/year-story/share" payload={sharePayload} label={`${y.label} year in review`} onClose={() => setSharing(false)} />}
        </>
      ) : <p className="text-sm text-mm-muted mt-3" data-testid="year-empty">No check-ins in this period yet.</p>}
    </div>
  );
};
